import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle, AlertCircle, ArrowLeft } from 'lucide-react';
import { Vendor } from '../../../types/vendor';
import { QuoteRequestForm } from '../../../components/forms/QuoteRequestForm.tsx';
import { QuoteRequestAPI } from '../../../services/api/quoteRequestApi.ts';

const QuoteRequestModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  vendor: Vendor;
}> = ({ isOpen, onClose, vendor }) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    weddingDate: '',
    isFlexible: false,
    guestCount: 0,
    phone: '',
    vision: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setShowSuccess(false);
    setShowError(false);
    
    try {
      const response = await QuoteRequestAPI.sendQuoteRequest(
        formData,
        vendor.id,
        vendor.email
      );

      if (response.success) {
        setShowSuccess(true);
        // Reset form after short delay
        setTimeout(() => {
          setFormData({
            firstName: '',
            lastName: '',
            weddingDate: '',
            isFlexible: false,
            guestCount: 0,
            phone: '',
            vision: ''
          });
          onClose();
        }, 2000);
      } else {
        setShowError(true);
        setErrorMessage(response.error || 'Failed to send message. Please try again.');
      }
    } catch (error) {
      console.error('Error sending quote request:', error);
      setShowError(true);
      setErrorMessage(error instanceof Error ? error.message : 'Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-xl flex items-center justify-center z-50">
      <motion.div
        initial={{ opacity: 0, y: 100 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 100 }}
        transition={{ type: "spring", damping: 25, stiffness: 300 }}
        className="w-full max-w-[280px] md:max-w-[480px] mx-auto bg-[#2A1F3C] overflow-hidden rounded-2xl"
      >
        {/* Compact Header */}
        <div className="bg-[#FF4E8E]/10 backdrop-blur-sm px-2 md:px-4 py-1 md:py-2 flex items-center justify-between border-b border-[#FF4E8E]/20">
          <button 
            onClick={onClose}
            className="p-1 md:p-1.5 rounded-full bg-[#FF4E8E]/80 backdrop-blur-sm border border-[#FF4E8E]/30 text-white active:bg-[#FF4E8E] transition-colors"
            aria-label="Back"
          >
            <ArrowLeft size={14} className="md:w-4 md:h-4" />
          </button>
          <h3 className="text-xs md:text-sm font-medium text-white">Message {vendor.name}</h3>
          <div className="w-6 md:w-8"></div>
        </div>
        
        <div className="px-2 md:px-4 py-1.5 md:py-3 overflow-y-auto" style={{ maxHeight: '80vh' }}>
          {/* Status Messages */}
          {showSuccess && (
            <div className="mb-1 md:mb-2 p-0.5 md:p-1 rounded bg-[#2DCEA8]/10 border border-[#2DCEA8]/30 flex items-center gap-1">
              <CheckCircle size={10} className="text-[#2DCEA8] md:w-4 md:h-4" />
              <p className="text-white/90 text-[10px] md:text-xs">Message sent successfully!</p>
            </div>
          )}
          
          {showError && (
            <div className="mb-1 md:mb-2 p-0.5 md:p-1 rounded bg-[#FF6B6B]/10 border border-[#FF6B6B]/30 flex items-center gap-1">
              <AlertCircle size={10} className="text-[#FF6B6B] md:w-4 md:h-4" />
              <p className="text-white/90 text-[10px] md:text-xs">{errorMessage}</p>
            </div>
          )}

          <QuoteRequestForm
            formData={formData}
            onFormDataChange={setFormData}
            onSubmit={handleSubmit}
            isSubmitting={isSubmitting}
            variant="modal"
          />
        </div>
      </motion.div>
    </div>
  );
};

export default QuoteRequestModal; 